import type {PageMigrator} from '../dataMigrationRunner'
import type {PageAPI} from '../../../page'
import {stripHashIfExists} from '../../../../utils/refArrayUtils'
import type {DalValue, Pointer} from '@wix/document-services-types'
import type {RelationshipsAPI} from '../../../relationships'
import type {DocumentManager} from '@wix/document-manager-core'
import type {ThemeExtAPI} from '../../../theme/theme'
import {isReferredId} from '../../../../utils/inflationUtils'
import {ReportableWarning} from '@wix/document-manager-utils'

const STYLE = 'style'
const REF_ARRAY = 'values'
const VARIANT_RELATION = 'VariantRelation'
const REF_ARRAY_TYPE = 'RefArray'
const REF_ARRAY_NAMESPACES = [STYLE]

interface Handler {
    createDefault(value: DalValue): Pointer | undefined
    shouldHaveDefault: boolean
}

type NameSpaceHandlers = Record<string, Handler>

const migratePage = (documentManager: DocumentManager, pageId: string) => {
    const {dal, extensionAPI, pointers} = documentManager
    const shouldMaintainReference = (pointer: Pointer, namespace: string): boolean => {
        const value: DalValue = dal.get(pointer)
        if (!value) return false
        if (value.type !== VARIANT_RELATION) return true
        const toPointer: Pointer = pointers.getPointer(stripHashIfExists(value.to), namespace)
        return dal.has(toPointer)
    }
    const getOwningComponent = (pointer: Pointer): DalValue => {
        const compPointerArr = (extensionAPI as RelationshipsAPI).relationships.getOwningReferencesToPointer(pointer)
        if (compPointerArr.length === 0) return
        return dal.get(compPointerArr[0])
    }
    const getNewRefArray = ({values}: DalValue, namespace: string): string[] => {
        return values.filter((value: string) => {
            const pointer = pointers.getPointer(stripHashIfExists(value), namespace)
            return shouldMaintainReference(pointer, namespace)
        })
    }
    const hasDefault = (refArray: string[], namespace: string) => {
        if (refArray.length === 0) return false
        return refArray.some(id => {
            const pointer = pointers.getPointer(stripHashIfExists(id), namespace)
            const {type} = dal.get(pointer)
            return type && type !== VARIANT_RELATION
        })
    }

    const removeUndefinedVariantRelations = (originalRefArray: string[], newRefArray: string[], namespace: string) => {
        originalRefArray.forEach((id: string) => {
            if (!newRefArray.includes(id)) dal.remove({id: stripHashIfExists(id), type: namespace})
        })
    }

    const getHandlerForNameSpace: NameSpaceHandlers = {
        [STYLE]: {
            createDefault: (dalValue: DalValue) => {
                const {theme} = extensionAPI as ThemeExtAPI
                const {skin, componentType} = dalValue
                if (!skin || !componentType) {
                    documentManager.logger.captureError(
                        new ReportableWarning({
                            errorType: 'componentHasNoSkinOrComponentType',
                            message: 'component is missing skin or component type',
                            extras: {dalValue}
                        })
                    )
                    return undefined
                }
                return theme.createStyleFromSkinAndType(componentType, skin, pageId)
            },
            shouldHaveDefault: true
        }
    }
    const pageCompFilter = (extensionAPI.page as PageAPI).getPageIndexId(pageId)

    REF_ARRAY_NAMESPACES.forEach(namespace => {
        const dataItemsToCheck: Record<string, DalValue> = dal.query(
            namespace,
            pageCompFilter,
            (v: any) => v?.type === REF_ARRAY_TYPE
        )
        Object.entries(dataItemsToCheck).forEach(([id, item]) => {
            let newRefArray = getNewRefArray(item, namespace)
            if (newRefArray.length && newRefArray.length === item.values.length) return
            if (getHandlerForNameSpace[namespace].shouldHaveDefault && !hasDefault(newRefArray, namespace)) {
                if (isReferredId(id) && !newRefArray.length) {
                    dal.remove({id, type: namespace}) //might create broken reference if used in effect or variables namespaces
                    return
                } else if (!isReferredId(id)) {
                    const refComponent = getOwningComponent({id, type: namespace})
                    if (!refComponent) {
                        documentManager.logger.interactionStarted('hasNoOwningComponent', {
                            id,
                            name: namespace
                        })
                        return
                    }
                    const defaultStylePointer = getHandlerForNameSpace[namespace]?.createDefault(refComponent)
                    if (!defaultStylePointer) return
                    newRefArray = [`#${defaultStylePointer.id}`, ...newRefArray]
                }
            }
            removeUndefinedVariantRelations(item.values, newRefArray, namespace)
            dal.set({id, type: namespace, innerPath: [REF_ARRAY]}, newRefArray)
        })
    })
}

const name = 'brokenRefArrayFixer'
const version = 1

export const brokenRefArrayFixer: PageMigrator = {
    migratePage,
    name,
    version,
    fixerRequiresReruns: false
}
